import type { Messages } from './messages';

export const fr: Messages = {
  'lang.name': 'Français',
  'lang.aria': 'Langue',
  'app.title': 'Synthétiseur web',

  // Header — mapping shift
  'header.shift.aria': 'Décaler le mappage du clavier',
  'shift.octDown': '− 8ve',
  'shift.noteDown': '− note',
  'shift.noteUp': '+ note',
  'shift.octUp': '+ 8ve',
  'shift.octDown.title': 'Une octave plus bas (touche −)',
  'shift.noteDown.title': 'Une note blanche plus bas (touche _)',
  'shift.noteUp.title': 'Une note blanche plus haut (touche +)',
  'shift.octUp.title': 'Une octave plus haut (touche =)',

  // Install button
  'install.prompt': 'Installer l’application',
  'install.installed': 'Installée',
  'install.promptTitle': 'Installer le synthétiseur comme application',
  'install.uninstallHint':
    'L’application est installée. Les navigateurs ne permettent pas de la désinstaller depuis une page ; utilisez le menu du navigateur (⋮) → « Désinstaller », ou les applications du système.',
  'install.manualHint':
    'Si elle n’apparaît pas automatiquement : dans le menu du navigateur (⋮), choisissez « Installer l’application ». Sur iPhone/iPad : Partager → « Sur l’écran d’accueil ».',

  // Metronome
  'metronome.label': 'Métronome',
  'metronome.tempoDown.aria': 'Tempo plus lent',
  'metronome.tempoUp.aria': 'Tempo plus rapide',
  'metronome.bpm.aria': 'Tempo (BPM)',
  'metronome.tap': 'Tap',
  'metronome.tap.aria': 'Battre le tempo en cliquant',
  'metronome.start': 'Démarrer',
  'metronome.stop': 'Arrêter',
  'meter.aria': 'Mesure (signature rythmique)',
  'meter.minus.aria': 'Moins de temps',
  'meter.plus.aria': 'Plus de temps',
  'meter.accents.aria': 'Accents sur les temps',
  'meter.custom': 'Personnalisé…',

  'sustain': 'Sustain',
  'reset.all': 'Réinitialiser tous les réglages',

  // Section titles
  'section.keyboard': 'Clavier',
  'section.engine': 'Moteur sonore',
  'section.sound': 'Instrument et son',
  'section.effects': 'Effets',
  'section.curve': 'Courbe de niveau sur le registre (grave ↔ aigu)',
  'section.offline': 'Disponibilité hors ligne',

  // Keyboard section
  'labels.aria': 'Étiquettes sur les touches',
  'label.cde': 'CDE',
  'label.cde.title': 'Lettres sur les touches (C D E …)',
  'label.doremi': 'DoReMi',
  'label.doremi.title': 'Solfège sur les touches (Do Ré Mi …)',
  'label.keys': 'Touches',
  'label.keys.title': 'La touche du clavier, sur chaque note',
  'label.octave': 'Octave',
  'label.octave.title': 'Le numéro de l’octave, en haut de la touche',
  'field.startNote': 'Note de départ',
  'field.startOctave': 'Octave de départ',
  'field.octaveCount': 'Nombre d’octaves',
  'field.displayMode': 'Mode d’affichage',
  'display.scroll': 'Défilement horizontal',
  'display.fit1': 'Ajustée sur une rangée',
  'display.fit2': 'Ajustée sur deux rangées',
  'range.label': 'Étendue du clavier',
  'range.keys': '{n} touches',
  'range.low.aria': 'Note la plus grave',
  'range.high.aria': 'Note la plus aiguë',
  'range.drag.aria': 'Déplacer toute l’étendue',
  'octaveCount.full': 'Étendue complète (88 touches)',
  'octaveCount.partial': '— (étendue partielle)',

  // Engine section
  'field.engine': 'Moteur',
  'engine.synth': 'Synthétisé (Tone.js)',
  'engine.sampler': 'Sample réel (instruments enregistrés)',
  'field.synthType': 'Type de synthétiseur',
  'field.preset': 'Preset',
  'preset.aria': 'Preset d’instrument',
  'field.waveform': 'Timbre',
  'waveform.aria': 'Forme d’onde (timbre)',
  'field.instrument': 'Instrument',
  'field.density': 'Densité des samples',
  'density.6': 'Rapide (1 sample / 6 demi-tons)',
  'density.3': 'Équilibré (1 sample / 3 demi-tons)',
  'density.1': 'Complet (tous les samples)',

  // Sampler status
  'status.loading': 'Chargement de « {instrument} » : {loaded} / {total} samples…',
  'status.upgrading': 'Jouable maintenant ({count} samples) · chargement de la qualité complète…',
  'status.ready': 'Prêt : {count} samples ({low}–{high}){failed}. Les notes intermédiaires sont transposées.',
  'status.readyFailed': ' · {failed} échoués',
  'status.cached': 'Instrument chargé (depuis le cache).',
  'status.errNoSamples': 'Cet instrument n’a aucun sample dans la bibliothèque.',
  'status.errLoadFailed': 'Impossible de charger le moindre sample (vérifiez votre connexion).',

  // Keyboard loading overlay
  'overlay.loading': 'Chargement de « {instrument} »… {loaded} / {total}',
  'overlay.preparing': 'Préparation de l’instrument…',
  'overlay.error': 'Impossible de charger les samples : {message}',

  // Sound knobs
  'knob.attack': 'Attaque (s)',
  'knob.release': 'Déclin (s)',
  'knob.volume': 'Volume',
  'knob.filter': 'Brillance',
  'knob.filter.aria': 'Brillance (filtre passe-bas)',
  'knob.echo': 'Echo',
  'knob.reverb': 'Reverb',
  'resetInstrument': 'Réinitialiser par défaut',
  'resetInstrument.title': 'Ramène le son de cet instrument à ses valeurs par défaut',

  // Filter tone words
  'filter.veryDark': 'très sombre',
  'filter.dark': 'sombre',
  'filter.neutral': 'neutre',
  'filter.bright': 'brillant',
  'filter.veryBright': 'très brillant',

  // Register curve
  'curve.hint':
    'Compensation de sonie perçue pour les oscillateurs synthétisés, qui sont techniquement plats mais paraissent pauvres dans le grave. Les instruments enregistrés apportent leur propre équilibre (mesuré plat à ~2,7 dB près de Do1 à Do7), c’est pourquoi la courbe ne leur est pas appliquée par défaut.',
  'curve.bassPre': 'Grave :',
  'curve.bassPost': 'dB / octave sous Do4',
  'curve.treblePre': 'Aigu :',
  'curve.treblePost': 'dB / octave au-dessus de Do4',
  'curve.onSamples': 'Appliquer aussi aux samples réels',
  'curve.notApplied': 'La courbe n’est pas appliquée au moteur de samples (cochez la case pour l’appliquer).',

  // Offline section
  'offline.hint':
    'Installez l’application depuis le menu du navigateur (« Installer » / « Sur l’écran d’accueil »), puis téléchargez tous les samples réels dans le cache. Le synthétiseur fonctionne ensuite entièrement hors ligne, sur n’importe quel appareil. Le téléchargement est de l’ordre de ~100–250 MB et ne se fait qu’une seule fois.',
  'offline.download': 'Tout télécharger pour le hors ligne',
  'offline.refresh': 'Actualiser le cache hors ligne',
  'offline.done': 'Tous les samples ont été téléchargés — disponibles hors ligne.',
  'offline.downloading': 'Téléchargement de {done} / {total} samples…{failed}',
  'offline.failedSuffix': ' ({failed} échoués)',
  'offline.doneAll': 'Terminé — les {total} samples sont disponibles hors ligne.',
  'offline.doneWithErrors': 'Téléchargé avec {failed} erreurs — relancez pour compléter le cache.',

  // Engine notices
  'engine.fallback': '« {label} » n’a pas pu être créé ({reason}). Retour à Synth.',
  'worklet.hint':
    'La page est ouverte directement depuis le disque (file://), où le navigateur refuse les modules AudioWorklet : PluckSynth et BitCrusher ne peuvent pas fonctionner. Lancez « npm run dev » et ouvrez l’adresse http qui y est indiquée pour qu’ils fonctionnent.',

  // Keyboard area
  'keyboard.aria': 'Clavier',
  'keyboard.resize.aria': 'Ajuster la hauteur du clavier (glisser, ou flèches haut/bas ; double-clic = automatique)',
  'keyboard.resize.title': 'Glisser pour la hauteur · double-clic = automatique',

  // Presets
  'preset.piano': 'Piano',
  'preset.organ': 'Orgue',
  'preset.strings': 'Cordes',
  'preset.bass': 'Basse',
  'preset.lead': 'Lead',
  'preset.custom': 'Personnalisé',

  // Synth types
  'synth.synth': 'Synth',
  'synth.monosynth': 'MonoSynth',
  'synth.amsynth': 'AMSynth',
  'synth.fmsynth': 'FMSynth',
  'synth.duosynth': 'DuoSynth',
  'synth.membrane': 'MembraneSynth',
  'synth.metal': 'MetalSynth',
  'synth.noise': 'NoiseSynth (monophonique)',
  'synth.pluck': 'PluckSynth (monophonique)',

  // Waveforms
  'waveform.sine': 'Sinusoïdale',
  'waveform.triangle': 'Triangulaire',
  'waveform.sawtooth': 'Dent de scie',
  'waveform.square': 'Carrée',

  // Effects
  'effect.mix': 'Mix',
  'effect.chorus': 'Chorus',
  'effect.distortion': 'Distorsion',
  'effect.bitcrusher': 'BitCrusher',
  'effect.vibrato': 'Vibrato',
  'effect.autowah': 'AutoWah',
  'effect.phaser': 'Phaser',
  'effect.pingpong': 'Ping-pong delay',
  'effect.stereowidener': 'Stereo widener',
  'effect.eq3': 'Égaliseur 3 bandes',
  'effect.pitchshift': 'Pitch shift',

  // Parameters (shared by effects and synths, keyed by param key)
  'param.frequency': 'Fréquence (Hz)',
  'param.distortion': 'Quantité',
  'param.bits': 'Bits',
  'param.depth': 'Profondeur',
  'param.octaves': 'Octaves',
  'param.delayTime': 'Temps (s)',
  'param.width': 'Largeur',
  'param.low': 'Grave (dB)',
  'param.mid': 'Médium (dB)',
  'param.high': 'Aigu (dB)',
  'param.pitch': 'Demi-tons',
  'param.harmonicity': 'Harmonicité',
  'param.modulationIndex': 'Indice de modulation',
  'param.vibratoAmount': 'Vibrato',
  'param.pitchDecay': 'Déclin tonal (s)',
  'param.dampening': 'Amortissement (Hz)',
  'param.resonance': 'Résonance',
  'param.type': 'Couleur du bruit',

  // Instruments
  'instrument.piano': 'Piano',
  'instrument.guitar-acoustic': 'Guitare acoustique',
  'instrument.guitar-nylon': 'Guitare à cordes nylon',
  'instrument.guitar-electric': 'Guitare électrique',
  'instrument.bass-electric': 'Basse électrique',
  'instrument.bassoon': 'Basson',
  'instrument.cello': 'Violoncelle',
  'instrument.clarinet': 'Clarinette',
  'instrument.contrabass': 'Contrebasse',
  'instrument.flute': 'Flûte',
  'instrument.french-horn': 'Cor d’harmonie',
  'instrument.harmonium': 'Harmonium',
  'instrument.harp': 'Harpe',
  'instrument.organ': 'Orgue',
  'instrument.saxophone': 'Saxophone',
  'instrument.trombone': 'Trombone',
  'instrument.trumpet': 'Trompette',
  'instrument.tuba': 'Tuba',
  'instrument.violin': 'Violon',
  'instrument.xylophone': 'Xylophone',

  // Meters
  'meter.4/4': '4/4 (commune)',
  'meter.3/4': '3/4 (valse)',
  'meter.2/4': '2/4',
  'meter.6/8': '6/8 (3+3)',
  'meter.5/4': '5/4 (Take Five · 3+2)',
  'meter.7/8': '7/8 (2+2+3)',
  'meter.9/8': '9/8 (balkanique · 2+2+2+3)',
};
